import { useEffect } from 'react';
import { ENUM_CARD_TYPE, getEmbedVendor, hasThirdPartyEmbed } from '@linkie/shared';
import { usePlaygroundState } from '@/modules/playground/store/playground.store';
import { CONSENT_CATEGORY, CONSENT_SURFACE, type OptionalConsentCategory } from '@/consent/contract';
import { configureConsent } from '@/core/lib/consent';
import { disclosureInventoryFor } from './disclosure-inventory';
import { isPublicPageRoute } from '@/core/utils/is-public-page-route';

const LINKIE_POLICY_URL = 'https://linkie.bio/privacy';

const ConsentConfigurator = () => {
  const isPaid = usePlaygroundState((s) => s.payload.is_paid);
  const cards = usePlaygroundState((s) => s.payload.cards);
  const mailingIntegrations = usePlaygroundState((s) => s.payload.mailing_integrations);
  const pagePolicyUrl = usePlaygroundState((s) => s.payload.privacy_policy_url);

  const isPublicPage = isPublicPageRoute();

  const embedCards = (cards ?? []).filter((card) => card.type === ENUM_CARD_TYPE.EMBED);

  const loadsThirdPartyEmbed = embedCards.some((card) => hasThirdPartyEmbed(card.embed_details?.html));

  const embedVendors = Array.from(
    new Set(
      embedCards
        .map((card) => getEmbedVendor(card.embed_details?.html)?.label)
        .filter((label): label is string => Boolean(label)),
    ),
  ).sort();

  const embedVendorKey = embedVendors.join(',');

  const collectsEmail = Boolean(mailingIntegrations?.length && mailingIntegrations[0].enabled);

  // On a public page the page owner is the controller, so their policy replaces ours when set.
  // Left undefined otherwise: the payload hydrates after this mounts, and overwriting would
  // undo the already-correct `data-policy-url` the OG middleware injected.
  const policyUrl = (isPublicPage ? pagePolicyUrl : LINKIE_POLICY_URL) || undefined;

  useEffect(() => {
    const categories: OptionalConsentCategory[] = [];

    if (isPublicPage) {
      if (collectsEmail) categories.push(CONSENT_CATEGORY.FUNCTIONAL);

      if (isPaid) categories.push(CONSENT_CATEGORY.ANALYTICS);

      categories.push(CONSENT_CATEGORY.PERFORMANCE);

      if (loadsThirdPartyEmbed) categories.push(CONSENT_CATEGORY.EMBEDS);
    }

    const surface = isPublicPage ? CONSENT_SURFACE.PUBLIC_PAGE : CONSENT_SURFACE.APP;

    configureConsent({
      surface,
      categories,
      vendors: { [CONSENT_CATEGORY.EMBEDS]: embedVendors },
      disclosures: disclosureInventoryFor(surface),
      ...(policyUrl ? { policyUrl } : {}),
      showSettingsPill: false,
    });
  }, [isPublicPage, isPaid, loadsThirdPartyEmbed, embedVendorKey, collectsEmail, policyUrl]);

  return null;
};

export default ConsentConfigurator;
